import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiActivity,
  FiAlertCircle,
  FiBarChart2,
  FiBookOpen,
  FiCheck,
  FiCheckCircle,
  FiCompass,
  FiCpu,
  FiEdit3,
  FiExternalLink,
  FiFileText,
  FiGlobe,
  FiLink,
  FiLoader,
  FiRefreshCw,
  FiSave,
  FiSearch,
  FiShield,
  FiSliders,
  FiTrendingUp,
  FiZap,
} from 'react-icons/fi';
import { api } from '../services/api';
import './AdminAiContentAgent.css';

const AUTHORITY = [
  {
    id: 'assistant',
    title: 'Assistant',
    fa: 'دستیار',
    description: 'فقط محتوا و پیشنهادها را آماده می‌کند؛ چیزی ذخیره یا منتشر نمی‌شود.',
    helper: 'کنترل کامل دستی',
  },
  {
    id: 'draft',
    title: 'Draft',
    fa: 'پیش‌نویس',
    description: 'مقاله و ترجمه‌ها را ذخیره می‌کند اما در سایت عمومی منتشر نمی‌کند.',
    helper: 'پیشنهاد هریاد',
    recommended: true,
  },
  {
    id: 'auto',
    title: 'Auto',
    fa: 'خودکار',
    description: 'پس از تولید، مقاله و ترجمه‌های فعال را مستقیماً منتشر می‌کند.',
    helper: 'انتشار مستقیم',
  },
];

const MODE_OPTIONS = [
  { id: 'latest', label: 'موضوعات و اخبار تازه' },
  { id: 'technology_updates', label: 'آپدیت فناوری‌ها' },
  { id: 'evergreen', label: 'موضوعات ماندگار' },
];

const MODEL_OPTIONS = [
  { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra' },
  { value: 'gpt-5.6-sol', label: 'GPT-5.6 Sol' },
  { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna' },
];

const WORKSPACES = [
  { id: 'builder', label: 'ساخت مقاله', icon: FiEdit3 },
  { id: 'ideas', label: 'ایده‌یابی', icon: FiCompass },
  { id: 'analysis', label: 'تحلیل محتوا', icon: FiBarChart2 },
  { id: 'settings', label: 'تنظیمات', icon: FiSliders },
  { id: 'history', label: 'تاریخچه', icon: FiActivity },
];

const QUICK_TOPICS = [
  'آخرین اخبار وب',
  'آخرین آپدیت‌های React',
  'تازه‌های WordPress',
  'موضوعات مهم SEO',
];

const CONTENT_OUTPUTS = [
  'عنوان SEO',
  'Meta Description',
  'Slug مناسب',
  'ساختار H1 / H2 / H3',
  'کلمات کلیدی اصلی و فرعی',
  'FAQ',
  'پیشنهاد لینک داخلی',
  'مقاله‌های مرتبط',
  'Alt تصاویر',
  'Schemaهای لازم',
  'پیشنهاد CTA',
  'خلاصه مقاله',
];

const formatNumber = (value) => Number(value || 0).toLocaleString('fa-IR');
const getArticleId = (article) => article?._id || article?.id || '';

const AdminAiContentAgent = ({
  articles = [],
  articleCategories = [],
  onUsePackage,
  onRefreshArticles,
  onOpenArticles,
}) => {
  const [bootLoading, setBootLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [notice, setNotice] = useState('');
  const [activeWorkspace, setActiveWorkspace] = useState('builder');
  const [settingsEnvelope, setSettingsEnvelope] = useState(null);
  const [settings, setSettings] = useState(null);
  const [usage, setUsage] = useState(null);
  const [runs, setRuns] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);

  const [ideaBrief, setIdeaBrief] = useState('');
  const [ideaMode, setIdeaMode] = useState('latest');
  const [ideaWeb, setIdeaWeb] = useState(true);
  const [ideas, setIdeas] = useState([]);
  const [ideaSources, setIdeaSources] = useState([]);
  const [ideaBusy, setIdeaBusy] = useState(false);

  const [generateBusy, setGenerateBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [generateForm, setGenerateForm] = useState({
    topic: '',
    brief: '',
    category: '',
    authority: 'draft',
    useWebSearch: true,
    multilingual: true,
    targetLocales: ['ckb', 'en'],
  });

  const [analysisArticleId, setAnalysisArticleId] = useState('');
  const [analysisBusy, setAnalysisBusy] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);

  const loadDashboard = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setBootLoading(true);
    setPageError('');
    try {
      const [settingsResponse, usageResponse, runsResponse] = await Promise.all([
        api.get('/api/ai-content/settings'),
        api.get('/api/ai-content/usage?days=30'),
        api.get('/api/ai-content/runs?limit=12'),
      ]);
      const envelope = settingsResponse.data || {};
      const nextSettings = envelope.settings || null;
      setSettingsEnvelope(envelope);
      setSettings(nextSettings);
      setUsage(usageResponse.data || null);
      setRuns(Array.isArray(runsResponse.data) ? runsResponse.data : []);
      if (nextSettings) {
        setIdeaWeb(Boolean(nextSettings.webResearchDefault));
        setGenerateForm((current) => ({
          ...current,
          authority: nextSettings.defaultAuthority || 'draft',
          useWebSearch: Boolean(nextSettings.webResearchDefault),
          multilingual: Boolean(nextSettings.multilingual?.enabled),
          targetLocales: Array.isArray(nextSettings.multilingual?.targetLocales)
            ? nextSettings.multilingual.targetLocales
            : ['ckb', 'en'],
        }));
      }
    } catch (error) {
      setPageError(error.message || 'بارگذاری Agent انجام نشد');
    } finally {
      if (!silent) setBootLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const localeOptions = useMemo(
    () => (settingsEnvelope?.locales || []).filter((locale) => locale.code !== 'fa'),
    [settingsEnvelope]
  );

  const selectedAuthority = useMemo(
    () => AUTHORITY.find((item) => item.id === generateForm.authority) || AUTHORITY[1],
    [generateForm.authority]
  );

  const selectedLocaleNames = useMemo(
    () => localeOptions
      .filter((locale) => generateForm.targetLocales.includes(locale.code))
      .map((locale) => locale.nativeName || locale.name || locale.code),
    [generateForm.targetLocales, localeOptions]
  );

  const patchSettings = (path, value) => {
    setSettings((current) => {
      if (!current) return current;
      const next = JSON.parse(JSON.stringify(current));
      const keys = path.split('.');
      let target = next;
      keys.slice(0, -1).forEach((key) => {
        if (!target[key] || typeof target[key] !== 'object') target[key] = {};
        target = target[key];
      });
      target[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    setNotice('');
    setPageError('');
    try {
      const response = await api.put('/api/ai-content/settings', settings);
      setSettings(response.data || settings);
      setNotice('تنظیمات Agent با موفقیت ذخیره شد.');
    } catch (error) {
      setPageError(error.message || 'ذخیره تنظیمات انجام نشد');
    } finally {
      setSavingSettings(false);
    }
  };

  const discoverIdeas = async () => {
    setIdeaBusy(true);
    setPageError('');
    setNotice('');
    try {
      const response = await api.post('/api/ai-content/ideas', {
        brief: ideaBrief,
        mode: ideaMode,
        useWebSearch: ideaWeb,
      });
      setIdeas(Array.isArray(response.data?.ideas) ? response.data.ideas : []);
      setIdeaSources(Array.isArray(response.data?.sources) ? response.data.sources : []);
      setNotice('پیشنهادهای موضوعی تازه آماده شدند.');
      loadDashboard({ silent: true });
    } catch (error) {
      setPageError(error.message || 'پیدا کردن موضوع انجام نشد');
    } finally {
      setIdeaBusy(false);
    }
  };

  const handleUseIdea = (idea) => {
    setGenerateForm((current) => ({
      ...current,
      topic: idea.title || '',
      brief: idea.angle || '',
      category: articleCategories.some((category) => category.name === idea.suggestedCategory)
        ? idea.suggestedCategory
        : current.category,
      useWebSearch: Boolean(idea.requiresWebResearch) || current.useWebSearch,
    }));
    setActiveWorkspace('builder');
    window.setTimeout(() => document.getElementById('ai-agent-generate-topic')?.focus(), 0);
  };

  const toggleGenerateLocale = (code) => {
    setGenerateForm((current) => ({
      ...current,
      targetLocales: current.targetLocales.includes(code)
        ? current.targetLocales.filter((item) => item !== code)
        : [...current.targetLocales, code],
    }));
  };

  const generateArticle = async () => {
    if (!generateForm.topic.trim()) {
      setPageError('موضوع مقاله را وارد کنید.');
      return;
    }
    setGenerateBusy(true);
    setPageError('');
    setNotice('');
    setResult(null);
    try {
      const response = await api.post('/api/ai-content/generate', generateForm);
      setResult(response.data || null);
      setNotice(response.message || 'مقاله آماده شد.');
      if (response.data?.article) await onRefreshArticles?.();
      loadDashboard({ silent: true });
      window.setTimeout(() => document.getElementById('ai-agent-result')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (error) {
      setPageError(error.message || 'تولید مقاله انجام نشد');
    } finally {
      setGenerateBusy(false);
    }
  };

  const analyzeExisting = async () => {
    if (!analysisArticleId) return;
    setAnalysisBusy(true);
    setAnalysisResult(null);
    setPageError('');
    try {
      const response = await api.post(`/api/ai-content/analyze/${analysisArticleId}`, { useWebSearch: false });
      setAnalysisResult(response.data || null);
      loadDashboard({ silent: true });
    } catch (error) {
      setPageError(error.message || 'تحلیل مقاله انجام نشد');
    } finally {
      setAnalysisBusy(false);
    }
  };

  if (bootLoading) {
    return (
      <div className="ai-agent-loading">
        <div className="ai-agent-loading-icon"><FiLoader className="is-spinning" /></div>
        <div><strong>در حال آماده‌سازی Agent هریاد</strong><span>تنظیمات و وضعیت سرویس در حال بارگذاری است…</span></div>
      </div>
    );
  }

  const totals = usage?.totals || {};
  const packageArticle = result?.package?.article || null;
  const translations = result?.package?.translations || [];
  const agentEnabled = Boolean(settings?.enabled);

  return (
    <div className="ai-agent-page" dir="rtl">
      <section className="ai-agent-readinessbar">
        <div className="ai-agent-readiness-copy">
          <span className={settingsEnvelope?.apiConfigured && agentEnabled ? 'is-ready' : 'is-attention'}><FiCpu /></span>
          <div>
            <strong>{settingsEnvelope?.apiConfigured && agentEnabled ? 'Agent آماده تولید محتواست' : 'Agent برای اجرا نیاز به بررسی دارد'}</strong>
            <small>حالت پیش‌فرض: {AUTHORITY.find((item) => item.id === settings?.defaultAuthority)?.fa || 'پیش‌نویس'} · مدل: {MODEL_OPTIONS.find((item) => item.value === settings?.model)?.label || settings?.model || '—'}</small>
          </div>
        </div>
        <div className="ai-agent-statuses">
          <span className={settingsEnvelope?.apiConfigured ? 'is-online' : 'is-warning'}>
            <i /> {settingsEnvelope?.apiConfigured ? 'OpenAI متصل' : 'API تنظیم نشده'}
          </span>
          <span className={agentEnabled ? 'is-online' : 'is-muted'}>
            <FiShield /> {agentEnabled ? 'Agent فعال' : 'Agent غیرفعال'}
          </span>
          <span><FiGlobe /> سه‌زبانه قابل کنترل</span>
        </div>
      </section>

      {pageError && <div className="ai-agent-notice is-error"><FiAlertCircle /> <span>{pageError}</span></div>}
      {notice && <div className="ai-agent-notice is-success"><FiCheckCircle /> <span>{notice}</span></div>}

      <section className="ai-agent-overview" aria-label="مصرف Agent در ۳۰ روز اخیر">
        <article>
          <span className="ai-agent-metric-icon"><FiActivity /></span>
          <div><small>اجرای Agent</small><strong>{formatNumber(totals.runs)}</strong></div>
        </article>
        <article>
          <span className="ai-agent-metric-icon"><FiTrendingUp /></span>
          <div><small>کل توکن</small><strong>{formatNumber(totals.totalTokens)}</strong></div>
        </article>
        <article>
          <span className="ai-agent-metric-icon"><FiGlobe /></span>
          <div><small>Web Search</small><strong>{formatNumber(totals.webSearchCalls)}</strong></div>
        </article>
        <article>
          <span className="ai-agent-metric-icon"><FiZap /></span>
          <div><small>درخواست مدل</small><strong>{formatNumber(totals.requests)}</strong></div>
        </article>
      </section>

      <nav className="ai-agent-workspace-nav" aria-label="بخش‌های Agent">
        {WORKSPACES.map((workspace) => {
          const Icon = workspace.icon;
          return (
            <button
              type="button"
              key={workspace.id}
              className={activeWorkspace === workspace.id ? 'is-active' : ''}
              onClick={() => setActiveWorkspace(workspace.id)}
              aria-current={activeWorkspace === workspace.id ? 'page' : undefined}
            >
              <Icon />
              <span>{workspace.label}</span>
            </button>
          );
        })}
      </nav>

      {activeWorkspace === 'builder' && (
        <>
          <section className="ai-agent-workspace-card">
            <header className="ai-agent-workspace-header">
              <div>
                <span className="ai-agent-step-label"><FiEdit3 /> Article Builder</span>
                <h3>مقاله جدید بساز</h3>
                <p>موضوع را مشخص کن؛ Agent بقیه مسیر محتوا و SEO را طبق سطح اختیار تو انجام می‌دهد.</p>
              </div>
              <span className="ai-agent-workspace-badge">۱۲ خروجی SEO و محتوا</span>
            </header>

            <div className="ai-agent-builder-layout">
              <div className="ai-agent-builder-main">
                <div className="ai-agent-form-section">
                  <div className="ai-agent-section-title">
                    <span>۱</span>
                    <div><strong>موضوع و جهت مقاله</strong><small>می‌توانی موضوع را مستقیم وارد کنی یا از ایده‌یابی کمک بگیری.</small></div>
                  </div>

                  <label className="ai-agent-field is-prominent">
                    <span>موضوع مقاله</span>
                    <input
                      id="ai-agent-generate-topic"
                      value={generateForm.topic}
                      onChange={(event) => setGenerateForm((current) => ({ ...current, topic: event.target.value }))}
                      placeholder="مثلاً آخرین آپدیت‌های React یا راهنمای افزایش سرعت وردپرس"
                    />
                  </label>

                  <div className="ai-agent-quick-topics" aria-label="موضوعات پیشنهادی سریع">
                    <span>شروع سریع:</span>
                    {QUICK_TOPICS.map((topic) => (
                      <button type="button" key={topic} onClick={() => setGenerateForm((current) => ({ ...current, topic, useWebSearch: true }))}>
                        {topic}
                      </button>
                    ))}
                    <button type="button" className="is-discovery" onClick={() => setActiveWorkspace('ideas')}><FiCompass /> ایده‌های بیشتر</button>
                  </div>

                  <label className="ai-agent-field">
                    <span>بریف و زاویه محتوا <small>اختیاری</small></span>
                    <textarea
                      rows="4"
                      value={generateForm.brief}
                      onChange={(event) => setGenerateForm((current) => ({ ...current, brief: event.target.value }))}
                      placeholder="مخاطب هدف، نکات مهم، لحن، محدودیت‌ها یا زاویه‌ای که می‌خواهی مقاله داشته باشد…"
                    />
                  </label>
                </div>

                <div className="ai-agent-form-section">
                  <div className="ai-agent-section-title">
                    <span>۲</span>
                    <div><strong>سطح اختیار Agent</strong><small>مشخص کن Agent بعد از تولید محتوا تا کجا اجازه پیشروی داشته باشد.</small></div>
                  </div>

                  <div className="ai-agent-authority-grid" role="radiogroup" aria-label="سطح اختیار Agent">
                    {AUTHORITY.map((item) => (
                      <button
                        type="button"
                        role="radio"
                        aria-checked={generateForm.authority === item.id}
                        key={item.id}
                        className={`${generateForm.authority === item.id ? 'is-active' : ''} ${item.id === 'auto' ? 'is-auto' : ''}`}
                        onClick={() => setGenerateForm((current) => ({ ...current, authority: item.id }))}
                      >
                        <div className="ai-agent-authority-top">
                          <span className="ai-agent-authority-check">{generateForm.authority === item.id ? <FiCheck /> : null}</span>
                          <div><strong>{item.fa}</strong><small>{item.title}</small></div>
                          {item.recommended && <em>پیشنهادی</em>}
                        </div>
                        <p>{item.description}</p>
                        <span className="ai-agent-authority-helper">{item.helper}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="ai-agent-form-section">
                  <div className="ai-agent-section-title">
                    <span>۳</span>
                    <div><strong>تحقیق، دسته‌بندی و زبان‌ها</strong><small>تنظیمات همین اجرا را بدون تغییر تنظیمات پیش‌فرض مشخص کن.</small></div>
                  </div>

                  <div className="ai-agent-option-grid">
                    <label className="ai-agent-field">
                      <span>دسته‌بندی</span>
                      <select
                        value={generateForm.category}
                        onChange={(event) => setGenerateForm((current) => ({ ...current, category: event.target.value }))}
                      >
                        <option value="">انتخاب خودکار Agent</option>
                        {articleCategories.map((category) => <option key={category.id || category._id || category.name} value={category.name}>{category.name}</option>)}
                      </select>
                    </label>

                    <label className={`ai-agent-switch-card ${generateForm.useWebSearch ? 'is-active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={generateForm.useWebSearch}
                        onChange={(event) => setGenerateForm((current) => ({ ...current, useWebSearch: event.target.checked }))}
                      />
                      <span className="ai-agent-switch-visual"><i /></span>
                      <span><FiSearch /><strong>تحقیق وب</strong><small>برای اخبار و اطلاعات روز</small></span>
                    </label>

                    <label className={`ai-agent-switch-card ${generateForm.multilingual ? 'is-active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={generateForm.multilingual}
                        onChange={(event) => setGenerateForm((current) => ({ ...current, multilingual: event.target.checked }))}
                      />
                      <span className="ai-agent-switch-visual"><i /></span>
                      <span><FiGlobe /><strong>تولید چندزبانه</strong><small>ترجمه‌ها همراه مقاله ساخته شوند</small></span>
                    </label>
                  </div>

                  {generateForm.multilingual && (
                    <div className="ai-agent-language-picker">
                      <span>زبان‌های خروجی</span>
                      <div className="ai-agent-locale-chips">
                        {localeOptions.map((locale) => (
                          <button
                            type="button"
                            key={locale.code}
                            className={generateForm.targetLocales.includes(locale.code) ? 'is-active' : ''}
                            onClick={() => toggleGenerateLocale(locale.code)}
                          >
                            <span className="ai-agent-chip-check"><FiCheck /></span>
                            {locale.nativeName || locale.name}
                            <small>{locale.code}</small>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="ai-agent-generate-bar">
                  <div>
                    <strong>{generateBusy ? 'Agent در حال ساخت مقاله است…' : 'آماده تولید مقاله'}</strong>
                    <span>{generateBusy ? 'تحقیق، ساختار، SEO و ترجمه در یک اجرای کنترل‌شده انجام می‌شود.' : 'قبل از اجرا، خلاصه تنظیمات را در ستون کناری بررسی کن.'}</span>
                  </div>
                  <button type="button" className="ai-agent-primary" onClick={generateArticle} disabled={generateBusy || !settingsEnvelope?.apiConfigured || !agentEnabled}>
                    {generateBusy ? <FiLoader className="is-spinning" /> : <FiZap />}
                    {generateBusy ? 'در حال ساخت…' : 'ساخت مقاله با Agent'}
                  </button>
                </div>
              </div>

              <aside className="ai-agent-run-summary">
                <div className="ai-agent-summary-head">
                  <span><FiSliders /></span>
                  <div><strong>خلاصه اجرای فعلی</strong><small>قبل از شروع یک نگاه نهایی</small></div>
                </div>

                <dl className="ai-agent-summary-list">
                  <div><dt>سطح اختیار</dt><dd className={`is-${generateForm.authority}`}>{selectedAuthority.fa}</dd></div>
                  <div><dt>تحقیق وب</dt><dd>{generateForm.useWebSearch ? 'فعال' : 'غیرفعال'}</dd></div>
                  <div><dt>دسته‌بندی</dt><dd>{generateForm.category || 'انتخاب خودکار'}</dd></div>
                  <div><dt>زبان‌ها</dt><dd>{generateForm.multilingual ? `فارسی${selectedLocaleNames.length ? ` + ${selectedLocaleNames.join(' + ')}` : ''}` : 'فقط فارسی'}</dd></div>
                </dl>

                {generateForm.authority === 'auto' && (
                  <div className="ai-agent-inline-warning"><FiAlertCircle /><span><strong>انتشار مستقیم فعال است</strong>مقاله پس از تولید بدون تأیید دستی منتشر می‌شود.</span></div>
                )}

                {!settingsEnvelope?.apiConfigured && (
                  <div className="ai-agent-inline-warning is-api"><FiCpu /><span><strong>OpenAI API متصل نیست</strong>برای اجرای Agent ابتدا کلید API را در سرور تنظیم کن.</span></div>
                )}

                {!agentEnabled && settingsEnvelope?.apiConfigured && (
                  <div className="ai-agent-inline-warning is-api"><FiShield /><span><strong>Agent غیرفعال است</strong>از تب تنظیمات، Agent را فعال کن.</span></div>
                )}

                <details className="ai-agent-output-checklist" open>
                  <summary>Agent چه چیزهایی می‌سازد؟ <span>{formatNumber(CONTENT_OUTPUTS.length)} مورد</span></summary>
                  <div>
                    {CONTENT_OUTPUTS.map((item) => <span key={item}><FiCheck /> {item}</span>)}
                  </div>
                </details>
              </aside>
            </div>
          </section>

          {result && packageArticle && (
            <section className="ai-agent-result" id="ai-agent-result">
              <header>
                <div className="ai-agent-result-title">
                  <span className="ai-agent-result-icon"><FiCheckCircle /></span>
                  <div>
                    <small>خروجی Agent آماده است</small>
                    <h3>{packageArticle.title}</h3>
                    <p>{packageArticle.summary}</p>
                  </div>
                </div>
                <div className={`ai-agent-result-status is-${result.authority}`}>
                  {result.authority === 'assistant' ? 'فقط پیشنهاد' : result.authority === 'draft' ? 'پیش‌نویس ذخیره شد' : 'منتشر شد'}
                </div>
              </header>

              <div className="ai-agent-result-metrics">
                <span><FiLink /><strong>{formatNumber(packageArticle.internalLinks?.length)}</strong> لینک داخلی</span>
                <span><FiBookOpen /><strong>{formatNumber(packageArticle.relatedArticleIds?.length)}</strong> مقاله مرتبط</span>
                <span><FiFileText /><strong>{formatNumber(packageArticle.faq?.length)}</strong> FAQ</span>
                <span><FiGlobe /><strong>{formatNumber(translations.length)}</strong> ترجمه</span>
              </div>

              <div className="ai-agent-result-grid">
                <article><small>SEO Title</small><strong>{packageArticle.seoTitle}</strong></article>
                <article><small>Meta Description</small><p>{packageArticle.metaDescription}</p></article>
                <article><small>Slug</small><code>{packageArticle.slug}</code></article>
                <article><small>کلمه کلیدی اصلی</small><strong>{packageArticle.primaryKeyword}</strong></article>
              </div>

              {(packageArticle.secondaryKeywords || []).length > 0 && (
                <div className="ai-agent-result-tags">
                  <span className="ai-agent-tags-label">کلمات فرعی</span>
                  {(packageArticle.secondaryKeywords || []).map((keyword) => <span key={keyword}>{keyword}</span>)}
                </div>
              )}

              <div className="ai-agent-result-actions">
                <div>
                  <small>مصرف این اجرا</small>
                  <span>{formatNumber(result.usage?.totalTokens)} توکن · {formatNumber(result.usage?.webSearchCalls)} جست‌وجوی وب</span>
                </div>
                <div className="ai-agent-action-group">
                  {result.authority === 'assistant' && (
                    <button type="button" className="ai-agent-primary" onClick={() => onUsePackage?.(packageArticle)}>
                      <FiEdit3 /> انتقال به ویرایشگر مقاله
                    </button>
                  )}
                  {result.article && (
                    <button type="button" className="ai-agent-secondary" onClick={onOpenArticles}>
                      <FiFileText /> مدیریت مقالات
                    </button>
                  )}
                </div>
              </div>

              {result.sources?.length > 0 && (
                <details className="ai-agent-sources is-result">
                  <summary>منابع تحقیق ({formatNumber(result.sources.length)})</summary>
                  <div>
                    {result.sources.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noopener noreferrer">{source.title || source.url}<FiExternalLink /></a>)}
                  </div>
                </details>
              )}
            </section>
          )}
        </>
      )}

      {activeWorkspace === 'ideas' && (
        <section className="ai-agent-workspace-card">
          <header className="ai-agent-workspace-header">
            <div>
              <span className="ai-agent-step-label"><FiCompass /> Topic Discovery</span>
              <h3>موضوع بعدی را هوشمند پیدا کن</h3>
              <p>یک حوزه یا فناوری را بده تا Agent موضوع‌های قابل انتشار و زاویه مناسب هر کدام را پیشنهاد کند.</p>
            </div>
          </header>

          <div className="ai-agent-ideas-layout">
            <div className="ai-agent-discovery-form">
              <label className="ai-agent-field is-prominent">
                <span>چه چیزی را بررسی کند؟</span>
                <textarea
                  rows="5"
                  value={ideaBrief}
                  onChange={(event) => setIdeaBrief(event.target.value)}
                  placeholder="مثلاً آخرین اخبار وب، آخرین آپدیت‌های React، موضوعات جدید WordPress…"
                />
              </label>

              <div className="ai-agent-quick-topics is-ideas">
                <span>پیشنهاد سریع:</span>
                {QUICK_TOPICS.map((topic) => <button type="button" key={topic} onClick={() => { setIdeaBrief(topic); setIdeaWeb(true); }}>{topic}</button>)}
              </div>

              <div className="ai-agent-option-grid is-two">
                <label className="ai-agent-field">
                  <span>نوع ایده</span>
                  <select value={ideaMode} onChange={(event) => setIdeaMode(event.target.value)}>
                    {MODE_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                  </select>
                </label>
                <label className={`ai-agent-switch-card ${ideaWeb ? 'is-active' : ''}`}>
                  <input type="checkbox" checked={ideaWeb} onChange={(event) => setIdeaWeb(event.target.checked)} />
                  <span className="ai-agent-switch-visual"><i /></span>
                  <span><FiGlobe /><strong>جست‌وجوی وب</strong><small>برای کشف موضوعات فعلی</small></span>
                </label>
              </div>

              <button type="button" className="ai-agent-primary is-full" onClick={discoverIdeas} disabled={ideaBusy || !settingsEnvelope?.apiConfigured || !agentEnabled}>
                {ideaBusy ? <FiLoader className="is-spinning" /> : <FiSearch />}
                {ideaBusy ? 'در حال بررسی و تحلیل…' : 'پیدا کردن ایده‌های مقاله'}
              </button>
            </div>

            <div className="ai-agent-idea-results">
              <div className="ai-agent-subhead">
                <div><strong>پیشنهادهای Agent</strong><small>{ideas.length ? `${formatNumber(ideas.length)} ایده آماده استفاده` : 'بعد از جست‌وجو، نتایج اینجا نمایش داده می‌شوند.'}</small></div>
                {ideas.length > 0 && <span>{MODE_OPTIONS.find((item) => item.id === ideaMode)?.label}</span>}
              </div>

              <div className="ai-agent-idea-list">
                {ideas.length === 0 ? (
                  <div className="ai-agent-empty-state"><span><FiCompass /></span><strong>هنوز ایده‌ای ساخته نشده</strong><p>یک موضوع کلی وارد کن تا Agent برایت فرصت‌های محتوایی پیدا کند.</p></div>
                ) : ideas.map((idea, index) => (
                  <article key={`${idea.title}-${index}`}>
                    <div className="ai-agent-idea-index">{formatNumber(index + 1)}</div>
                    <div className="ai-agent-idea-body">
                      <div className="ai-agent-idea-topline">
                        <span className={`ai-agent-freshness is-${idea.freshness}`}>{idea.freshness}</span>
                        {idea.searchIntent && <span className="ai-agent-intent">{idea.searchIntent}</span>}
                      </div>
                      <h4>{idea.title}</h4>
                      <p>{idea.angle}</p>
                      <div className="ai-agent-idea-meta">
                        {idea.primaryKeyword && <span>Keyword: {idea.primaryKeyword}</span>}
                        {idea.suggestedCategory && <span>{idea.suggestedCategory}</span>}
                      </div>
                    </div>
                    <button type="button" className="ai-agent-use-idea" onClick={() => handleUseIdea(idea)}>استفاده در مقاله <FiExternalLink /></button>
                  </article>
                ))}
              </div>

              {ideaSources.length > 0 && (
                <details className="ai-agent-sources">
                  <summary>منابع تحقیق موضوعات ({formatNumber(ideaSources.length)})</summary>
                  <div>{ideaSources.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noopener noreferrer">{source.title || source.url}<FiExternalLink /></a>)}</div>
                </details>
              )}
            </div>
          </div>
        </section>
      )}

      {activeWorkspace === 'analysis' && (
        <section className="ai-agent-workspace-card">
          <header className="ai-agent-workspace-header">
            <div>
              <span className="ai-agent-step-label"><FiBarChart2 /> Content Audit</span>
              <h3>مقاله‌های قبلی را تحلیل کن</h3>
              <p>SEO، تازگی محتوا، ساختار و فرصت‌های لینک‌سازی داخلی یک مقاله را بررسی کن.</p>
            </div>
          </header>

          <div className="ai-agent-analysis-shell">
            <div className="ai-agent-analysis-controls">
              <label className="ai-agent-field">
                <span>مقاله مورد نظر</span>
                <select value={analysisArticleId} onChange={(event) => setAnalysisArticleId(event.target.value)}>
                  <option value="">یک مقاله را انتخاب کنید</option>
                  {articles.map((article) => <option key={getArticleId(article)} value={getArticleId(article)}>{article.title}</option>)}
                </select>
              </label>
              <button type="button" className="ai-agent-primary" onClick={analyzeExisting} disabled={!analysisArticleId || analysisBusy || !agentEnabled}>
                {analysisBusy ? <FiLoader className="is-spinning" /> : <FiActivity />}
                {analysisBusy ? 'در حال تحلیل…' : 'تحلیل مقاله'}
              </button>
            </div>

            {!analysisResult?.analysis && (
              <div className="ai-agent-empty-state is-analysis"><span><FiBarChart2 /></span><strong>تحلیل محتوایی هنوز انجام نشده</strong><p>مقاله‌ای را انتخاب کن تا Agent نقاط قوت، مشکلات و پیشنهادهای بهبود را مشخص کند.</p></div>
            )}

            {analysisResult?.analysis && (
              <div className="ai-agent-analysis-result">
                <div className="ai-agent-score-card">
                  <span>امتیاز کلی محتوا</span>
                  <strong>{formatNumber(analysisResult.analysis.score)}</strong>
                  <small>از ۱۰۰</small>
                </div>
                <div className="ai-agent-issues">
                  <div className="ai-agent-subhead"><div><strong>موارد نیازمند توجه</strong><small>اولویت‌ها را از بالا به پایین بررسی کن.</small></div></div>
                  {(analysisResult.analysis.issues || []).slice(0, 8).map((issue, index) => (
                    <article key={`${issue.area}-${index}`} className={`is-${issue.severity}`}>
                      <span className="ai-agent-issue-level">{issue.severity}</span>
                      <div><small>{issue.area}</small><strong>{issue.finding}</strong><p>{issue.recommendation}</p></div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {activeWorkspace === 'settings' && (
        <section className="ai-agent-workspace-card">
          <header className="ai-agent-workspace-header">
            <div>
              <span className="ai-agent-step-label"><FiSliders /> Agent Settings</span>
              <h3>رفتار پیش‌فرض Agent</h3>
              <p>این تنظیمات نقطه شروع اجرای جدید هستند و در هر مقاله می‌توانی آن‌ها را موقتاً تغییر بدهی.</p>
            </div>
          </header>

          {settings && (
            <div className="ai-agent-settings-layout">
              <div className="ai-agent-settings-main">
                <section className="ai-agent-settings-group">
                  <div className="ai-agent-subhead"><div><strong>وضعیت و مدل</strong><small>دسترسی و موتور پیش‌فرض Agent</small></div></div>
                  <label className={`ai-agent-settings-switch ${settings.enabled ? 'is-active' : ''}`}>
                    <span><FiShield /><span><strong>Agent فعال باشد</strong><small>دسترسی به تمام عملیات تولید و تحلیل AI</small></span></span>
                    <input type="checkbox" checked={Boolean(settings.enabled)} onChange={(event) => patchSettings('enabled', event.target.checked)} />
                    <i />
                  </label>
                  <div className="ai-agent-option-grid is-two">
                    <label className="ai-agent-field">
                      <span>مدل پیش‌فرض</span>
                      <select value={settings.model || ''} onChange={(event) => patchSettings('model', event.target.value)}>
                        {MODEL_OPTIONS.map((model) => <option key={model.value} value={model.value}>{model.label}</option>)}
                      </select>
                    </label>
                    <label className="ai-agent-field">
                      <span>Reasoning</span>
                      <select value={settings.reasoningEffort || 'medium'} onChange={(event) => patchSettings('reasoningEffort', event.target.value)}>
                        <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                      </select>
                    </label>
                  </div>
                </section>

                <section className="ai-agent-settings-group">
                  <div className="ai-agent-subhead"><div><strong>رفتار پیش‌فرض اجرا</strong><small>برای تولید مقاله‌های جدید</small></div></div>
                  <label className="ai-agent-field">
                    <span>سطح اختیار پیش‌فرض</span>
                    <select value={settings.defaultAuthority || 'draft'} onChange={(event) => patchSettings('defaultAuthority', event.target.value)}>
                      {AUTHORITY.map((item) => <option key={item.id} value={item.id}>{item.fa} ({item.title})</option>)}
                    </select>
                  </label>
                  <label className={`ai-agent-settings-switch ${settings.webResearchDefault ? 'is-active' : ''}`}>
                    <span><FiSearch /><span><strong>تحقیق وب پیش‌فرض</strong><small>در ساخت مقاله امکان Web Search فعال باشد</small></span></span>
                    <input type="checkbox" checked={Boolean(settings.webResearchDefault)} onChange={(event) => patchSettings('webResearchDefault', event.target.checked)} />
                    <i />
                  </label>
                  <label className={`ai-agent-settings-switch ${settings.multilingual?.enabled ? 'is-active' : ''}`}>
                    <span><FiGlobe /><span><strong>تولید چندزبانه پیش‌فرض</strong><small>ترجمه‌های فعال همراه مقاله فارسی ساخته شوند</small></span></span>
                    <input type="checkbox" checked={Boolean(settings.multilingual?.enabled)} onChange={(event) => patchSettings('multilingual.enabled', event.target.checked)} />
                    <i />
                  </label>
                </section>
              </div>

              <aside className="ai-agent-settings-aside">
                <div className="ai-agent-settings-preview">
                  <span><FiCpu /></span>
                  <strong>پروفایل فعلی Agent</strong>
                  <dl>
                    <div><dt>وضعیت</dt><dd>{settings.enabled ? 'فعال' : 'غیرفعال'}</dd></div>
                    <div><dt>مدل</dt><dd>{MODEL_OPTIONS.find((item) => item.value === settings.model)?.label || settings.model}</dd></div>
                    <div><dt>اختیار</dt><dd>{AUTHORITY.find((item) => item.id === settings.defaultAuthority)?.fa || 'پیش‌نویس'}</dd></div>
                    <div><dt>Web Search</dt><dd>{settings.webResearchDefault ? 'فعال' : 'غیرفعال'}</dd></div>
                    <div><dt>چندزبانه</dt><dd>{settings.multilingual?.enabled ? 'فعال' : 'غیرفعال'}</dd></div>
                  </dl>
                </div>
                <button type="button" className="ai-agent-save-settings" onClick={saveSettings} disabled={savingSettings}>
                  {savingSettings ? <FiLoader className="is-spinning" /> : <FiSave />}
                  {savingSettings ? 'در حال ذخیره…' : 'ذخیره تنظیمات Agent'}
                </button>
              </aside>
            </div>
          )}
        </section>
      )}

      {activeWorkspace === 'history' && (
        <section className="ai-agent-workspace-card">
          <header className="ai-agent-workspace-header">
            <div>
              <span className="ai-agent-step-label"><FiActivity /> Activity Log</span>
              <h3>آخرین فعالیت‌های Agent</h3>
              <p>اجرای تولید، ایده‌یابی و تحلیل را همراه با وضعیت و مصرف هر اجرا مرور کن.</p>
            </div>
            <button type="button" className="ai-agent-refresh" onClick={() => loadDashboard({ silent: true })}><FiRefreshCw /> بروزرسانی</button>
          </header>

          <div className="ai-agent-runs-table">
            <div className="ai-agent-runs-head"><span>نوع</span><span>موضوع</span><span>اختیار</span><span>مصرف</span><span>وضعیت</span></div>
            {runs.length === 0 ? (
              <div className="ai-agent-empty-state"><span><FiActivity /></span><strong>هنوز اجرایی ثبت نشده</strong><p>بعد از اولین اجرای Agent، تاریخچه اینجا نمایش داده می‌شود.</p></div>
            ) : runs.map((run) => (
              <div className="ai-agent-run-row" key={run._id}>
                <span className="ai-agent-run-type">{run.type === 'generate' ? 'تولید مقاله' : run.type === 'ideas' ? 'ایده‌یابی' : 'تحلیل'}</span>
                <strong>{run.topic || run.brief || '—'}</strong>
                <span>{AUTHORITY.find((item) => item.id === run.authority)?.fa || run.authority || '—'}</span>
                <span>{formatNumber(run.usage?.totalTokens)} توکن</span>
                <span className={`ai-agent-run-status ${run.status === 'completed' ? 'is-success' : 'is-error'}`}><i />{run.status === 'completed' ? 'موفق' : 'ناموفق'}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default AdminAiContentAgent;
